import { CommonModule } from '@angular/common';
import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, ActivatedRoute } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { AutoFocusDirective } from '../directives/auto-focus.directive';
import { LoggerService } from '../shared/logger.service';
import { ToastrService } from 'ngx-toastr';
import { SettingsService } from '../settings/settings.service';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { CasiCloseOptions, ResetStages } from '../common/constants.lib';
import { zeroPadNumber } from '../common/utils.lib';
import { confirmedPasswordValidator } from '../directives/casi.validators';
import { BehaviorSubject, Subscription } from 'rxjs';
import { RuleEval, Settings } from '../common/obj.lib';
import { UserDataService } from '../users/user-data.service';
import { ErrorDialogModalComponent } from '../common-components/error-dialog-modal/error-dialog-modal.component';
import { DialogCloseResult, DialogService } from '@progress/kendo-angular-dialog';
import { InactiveAlertComponent } from '../auth/inactive-alert/inactive-alert.component';
import { User } from '../models/models.lib';

@Component({
  selector: 'casi-reset-pw',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    RouterLink,
    RouterLinkActive,
    LabelModule,
    InputsModule,
    ButtonsModule,
    AutoFocusDirective,
    ErrorDialogModalComponent,
    InactiveAlertComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './reset-pw.component.html',
  styleUrl: './reset-pw.component.scss'
})
export class ResetPwComponent implements OnInit, OnDestroy {
  noneForm: FormGroup;
  smsForm: FormGroup;
  resetForm: FormGroup;

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  settings: Settings = this.settingService.getSettings();

  stage: ResetStages = ResetStages.None;

  b1: boolean = false;
  b2: boolean = false;

  timeleft: string = '2:00';
  private timerHandle: ReturnType<typeof setInterval> | undefined;
  accessToken: string = '';

  _smsAuth: string = '';
  confirmPassword: string = '';
  _password: string = '';

  private _busy = new BehaviorSubject<boolean>(false);
  get busy() {
    return this._busy.getValue();
  }
  set busy(val: boolean) {
    this._busy.next(val);
  }

  private _checking = new BehaviorSubject<boolean>(false);
  get checking() {
    return this._checking.getValue();
  }
  set checking(val: boolean) {
    this._checking.next(val);
  }

  private _codeError = new BehaviorSubject<boolean>(false);
  get codeError() {
    return this._codeError.getValue();
  }
  set codeError(val: boolean) {
    this._codeError.next(val);
  }
  // codeError: boolean = false;
  userEmail: string = this.settingService.lastLogin;
  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    private fb: NonNullableFormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private settingService: SettingsService,
    public faIcons: FaIconLibrary,
    private userDataService: UserDataService,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private viewContainerRef: ViewContainerRef
  ) {
    faIcons.addIcons(...all);
    this.noneForm = this.fb.group({
      email: [this.settingService.lastLogin, [Validators.required, Validators.email]],
    });
    this.smsForm = this.fb.group({
      email: [this.settingService.lastLogin, [Validators.required, Validators.email]],
      smsauth: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
    this.resetForm = this.fb.group({
      password: [null, [Validators.required, Validators.minLength(6)]],
      confirmedPassword: [null, [Validators.required]],
    },
      {
        validators: confirmedPasswordValidator
      }
    );

    this._subs.push(this.route.queryParams.subscribe(params => {
      let qmail = params['email'];
      if (qmail) {
        this.noneForm.patchValue({ email: qmail });
        this.smsForm.patchValue({ email: qmail });
      }
    }));
  }

  //#region Form fields

  get noneF() {
    return this.noneForm.controls;
  }

  get smsF() {
    return this.smsForm.controls;
  }

  get f() {
    return this.resetForm.controls;
  }

  get noneEmail() {
    return this.noneForm.get('email');
  }

  get smsEmail() {
    return this.smsForm.get('email');
  }

  get smsAuth() {
    return this.smsForm.get('smsauth');
  }
  get password() {
    return this.resetForm.get('password');
  }
  get confirmedPassword() {
    return this.resetForm.get('confirmedPassword');
  }

  //#endregion

  //#region NG Handlers

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.logger.debug(`ngOnDestroy...`);
    this.stopCountdown();
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Sumbit Handlers

  submitNoneForm = () => {
    const { email } = this.noneForm.value;
    this.logger.debug(`submitNoneForm: `, email);
    this.userEmail = email;
    this.busy = true;
    // this.notify('Submitted password reset request.');
    // this.router.navigate(['/login']);
    this.emailExists(email)
      .then(checked => {
        this.logger.debug(`submitNoneForm:checked: `, checked);
        if (checked && checked.active === true) {
          this.smsForm.patchValue({ email, smsauth: '' });
          this.resetForm.patchValue({ password: null, confirmPassword: null });
          this._smsAuth = '';
          this.confirmPassword = '';
          this._password = '';
          // this.setStage(ResetStages.SMSReceived);
          this.initSmsRequest(email);
        } else {
          //alert user
          if (!checked) {
            this.logger.debug(`submitNoneForm:alert user that email doesn't exists...`);
            this.openErrorDailog("Email does not exist", `The email ${email} does not exist.`, "Close and Retry")

          }
          if (checked?.active === false) {
            this.openInactiveAlert();
            this.setStage(ResetStages.None);
          }
          this.busy = false;
        }
      })
    // return;
  };

  submitSmsForm = () => {
    this.checking = true;
    const { email, smsauth } = this.smsForm.value;
    this.logger.debug(`vm.submitSmsForm: `, email, smsauth);
    this.codeError = false;
    return this.checkCode(email, smsauth)
      .then((resp: any) => {
        if (resp) {
          this.setStage(ResetStages.AccessTokenReceived);
          this.stopCountdown();
        } else {
          this.notify('Incorrect code submitted', true);
          this.setStage(ResetStages.SMSReceived);
          this.codeError = true;
        }
      })
      .catch((err: any) => {
        this.logger.error(`submitSmsForm:error: `, err);
        this.codeError = true;
        this.notify('Problem with code entered', true);
      })
      .finally(() => this.checking = false);
  };

  resendCode = () => {
    this.busy = true;
    this.logger.debug(`resendCode...`);
    this.smsForm.patchValue({ smsauth: '' });
    this.initSmsRequest(this.userEmail);
  };

  submitPWChange = () => {
    this.busy = true;
    const { password, confirmPassword } = this.resetForm.value;
    this.logger.debug(`submitPWChange: `, { pw: password, confirm: confirmPassword });
    return this.userDataService.resetPassword(this.accessToken, password)
      .then(resp => {
        this.logger.debug(`submitPWChange: resp: `, resp);
        this.notify('Password changed');
        this.router.navigate(['/login']);
      })
      .catch(err => {
        this.logger.error(`submitPWChange: error: `, err);
        this.notify(`Problem changing password`, true);
      })
      .finally(() => this.busy = false)
  };

  onSubmit(): void {
    this.logger.debug('onSubmit...form.value', this.noneForm.value);
    this.notify('Submitted password reset request.');
    this.router.navigate(['/login']);
  }

  //#endregion

  //#region API Methods

  initSmsRequest = (email: string) => {
    this.codeError = false;
    this.userDataService.requestSMSToken(email)
      .then(resp => {
        this.logger.debug(`initSmsRequest: resp: `, resp);
        const { active } = resp;
        if (active) {
          this.setStage(ResetStages.SMSReceived);
          this.toggleMsg();
          this.resetCountdown();
        } else {
          throw new Error('Account inactive');
        }
        // this.countdown();
      })
      .catch(err => {
        this.logger.error(`initSmsRequest: error`, err.message);
        this.stopCountdown();
        if (err.message.toLowerCase().indexOf("inactive") !== -1) {
          this.openInactiveAlert();
          this.setStage(ResetStages.None);
        }
      })
      .finally(() => this.busy = false);
  }

  checkCode = (email: string, code: string) =>
    this.userDataService.requestAccessToken(email, code)
      .then(resp => {
        this.logger.debug(`checkCode: resp`, resp);
        this.accessToken = resp;
        return resp;
      })
      .catch(err => {
        this.logger.error(`checkCode: error: `, err);
        return err;
      })
      .finally(() => this.busy = false);

  //#endregion

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private stopCountdown() {
    this.logger.debug('stopCountdown...');
    if (this.timerHandle)
      clearInterval(this.timerHandle);
    this.timerHandle = undefined;
  }

  private countdown() {
    this.logger.debug('countdown...');
    let remaining = 120;
    this.stopCountdown();
    this.timerHandle = setInterval(() => {
      remaining--;
      let min = Math.trunc(remaining / 60);
      let sec = remaining % 60;
      let secAsString = sec.toString();
      if (+sec < 10)
        secAsString = zeroPadNumber(sec); // '0' + sec;
      this.timeleft = `${min}:${secAsString}`;
      // this.logger.debug(`time left: ${this.timeleft}`);
      this.toggleMsg();
      // $("#count_timer").text(vm.timeleft);
      if (remaining <= 0)
        this.stopCountdown();
    }, 1000);
  }

  private resetCountdown() {
    this.logger.debug('resetCountdown...');
    this.stopCountdown();
    this.countdown();
  }

  private toggleMsg() {
    // this.logger.debug(`toggleMsg: `, this.timeleft);

    if (this.timeleft === '0:00') {
      this.b1 = false;
      this.b2 = true;
    } else {
      this.b1 = true;
      this.b2 = false;
    }
  }

  private async emailExists(email: string): Promise<User | undefined> {
    return this.userDataService.checkEmailExists(email)
      .then(found => {
        this.logger.debug(`emailExists: ${email}:found: `, found);
        if (found)
          return User.create(found);
        return undefined;
        // if (found && found.active === true) {
        //   return true;
        // }
        // return false;
      })
      .catch(err => {
        this.logger.error(`emailExists: error: `, err.message);
        return undefined;
      })
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close', buttonText2: string = ''): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class",
      appendTo: this.viewContainerRef,
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
    contentRef.buttonText2 = buttonText2;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ");
      }
    });

  }

  private setStage(stage: ResetStages = ResetStages.None) { setTimeout(() => this.stage = stage) };

  private openInactiveAlert(): void {
    const dialogRef = this.dialogService.open({
      content: InactiveAlertComponent,
      minWidth: 320,
      // width: 450,
      // cssClass: "custom-css-class"
      appendTo: this.viewContainerRef,
    });

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openInactiveAlert:result:', { r, instance: r instanceof DialogCloseResult });
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openInactiveAlert:ok... ");
        return;
      }
      if (r.text === CasiCloseOptions.Navigate) {
        this.logger.debug(`openInactiveAlert:navigate...`);
        this.router.navigate(['/contact-us']);
        return;
      }
    });

  }

}
